import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { ProductEventDataList } from 'corgi-types';
import { space, textAlign, width } from 'styled-system';

import type { ButtonKind } from 'common/components/Button';
import { ButtonLink } from 'common/components/Button';
import { FlexJustifyContent, Grid } from 'common/components/Grid';
import RichContent from 'common/components/RichText/RichContent';
import type { Product } from 'common/types/product-card';
import { Element } from 'common/types/tracking';
import { dispatchCtaButtonsClickEvent } from '../../cta-buttons/components/tracking';
import ProductCardList from './ProductCardList';

export interface ContentProductListProps {
  heading: string;
  isHeadingCenterAligned: boolean;
  ctaLabel: string;
  ctaHref: string;
  ctaType: ButtonKind;
  articles: Product[];
}

const COLUMNS_COUNT = 4;

const Heading = styled(RichContent)<any>`
  ${textAlign};
  ${width};
  ${space};
`;

const headingStyles: ComponentProps<typeof Heading> = {
  textAlign: 'left',
  width: 1,
  py: 'sp_8',
  mb: [null, 'sp_12', 'sp_16', 'sp_24'],
};

const SeeMoreButton = styled(ButtonLink)<any>`
  ${space};
`;

const seeMoreButtonStyles: ComponentProps<typeof SeeMoreButton> = {
  kind: 'secondary',
  py: 'sp_8',
  px: 'sp_8',
};

const ContentProductList = ({
  heading,
  isHeadingCenterAligned,
  ctaLabel,
  ctaHref,
  ctaType,
  articles,
}: ContentProductListProps) => {
  const handleCtaClick = () => {
    dispatchCtaButtonsClickEvent(ctaLabel, Element.Button);
  };

  return (
    <Grid
      data-corgi-id="content-product-list"
      flexDirection="column"
      justifyContent={FlexJustifyContent.CENTER}
    >
      {heading && (
        <Heading
          {...headingStyles}
          {...(isHeadingCenterAligned ? { textAlign: 'center' } : {})}
          content={heading}
        />
      )}
      <ProductCardList
        articles={articles}
        columnsCount={COLUMNS_COUNT}
        productEventDataListType={ProductEventDataList.ContentList}
      />
      {ctaHref && ctaLabel && (
        <SeeMoreButton
          {...seeMoreButtonStyles}
          kind={ctaType}
          href={ctaHref}
          onClick={handleCtaClick}
        >
          {ctaLabel}
        </SeeMoreButton>
      )}
    </Grid>
  );
};

export default ContentProductList;
